import { useCallback, useEffect, useState } from 'react';
import type { Brief } from '@studio/shared';
import { BriefForm } from './components/BriefForm.js';
import { CodePanel } from './components/CodePanel.js';
import { GameFrame } from './components/GameFrame.js';
import { JamTimer } from './components/JamTimer.js';
import { Arcade } from './components/Arcade.js';
import { SproutForm } from './components/SproutForm.js';
import { TweakPanel } from './components/TweakPanel.js';
import { PitchMode } from './components/PitchMode.js';
import { SignIn } from './components/SignIn.js';
import { BuildingScreen } from './components/BuildingScreen.js';
import { StartJam } from './components/StartJam.js';
import type { Tunable } from '@studio/shared';
import { canOfferRepair, type GameState } from './gameEvents.js';
import * as api from './api.js';

type Made = { buildId: string; gameUrl: string; brief: Brief; templateId: string };

type Tab = 'make' | 'arcade' | 'pitch';
const TABS: Tab[] = ['make', 'arcade', 'pitch'];

const tabFromHash = (): Tab => {
  const hash = window.location.hash.replace('#', '');
  return (TABS as string[]).includes(hash) ? (hash as Tab) : 'make';
};

const EMPTY_BRIEF: Brief = {
  world: '', character: '', action: '', winCondition: '', loseCondition: '',
};

export function App() {
  const [me, setMe] = useState<api.Me | null>(null);
  const [made, setMade] = useState<Made | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [coach, setCoach] = useState<{ score: number | null; question: string | null } | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [tunables, setTunables] = useState<Tunable[]>([]);
  const [buildingWhat, setBuildingWhat] = useState("");
  const [focusLine, setFocusLine] = useState<number | null>(null);
  // Tabs live in the URL hash so they can be linked, bookmarked and reached
  // with the back button — and so a child can be sent straight to the arcade.
  const [tab, setTab] = useState<Tab>(() => tabFromHash());
  const [myGames, setMyGames] = useState<api.ArcadeGame[]>([]);
  const GAMES_ORIGIN = (import.meta.env.VITE_GAMES_ORIGIN as string) ?? 'http://localhost:8788';

  useEffect(() => {
    api.getMe().then(setMe).catch(() => setProblem('needs-sign-in'));
    api
      .getArcade()
      .then((r) => setMyGames(r.games))
      .catch(() => setMyGames([]));
  }, []);

  // Source comes from the authenticated API, never from the games origin — that
  // origin sends no CORS headers by design, and its bundles are not ours to read
  // cross-origin. Nothing here is executed; it is rendered as text.
  useEffect(() => {
    if (!made || !showCode || source) return;
    api
      .getSource(made.buildId)
      .then((r) => setSource(r.html))
      .catch(() => setSource(null));
  }, [made, showCode, source]);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const goToTab = useCallback((next: Tab) => {
    setTab(next);
    // replaceState rather than a hash assignment: switching tabs should not
    // stack up history entries a child then has to back out through.
    window.history.replaceState({}, '', next === 'make' ? window.location.pathname : `#${next}`);
  }, []);

  useEffect(() => {
    if (!made) {
      setTunables([]);
      return;
    }
    api
      .getTunables(made.buildId)
      .then((r) => setTunables(r.tunables))
      .catch(() => setTunables([]));
  }, [made]);

  const run = useCallback(
    async (input: Parameters<typeof api.build>[0]) => {
      setBusy(true);
      setProblem(null);
      setBuildingWhat(input.brief.world || input.brief.character || "your game");
      try {
        const result = await api.build(input);
        if (result.ok) {
          setMade({
            buildId: result.buildId,
            gameUrl: result.gameUrl,
            brief: input.brief,
            templateId: input.templateId,
          });
          setCoach({ score: result.coachScore, question: result.coachQuestion });
          setSource(null);
          setGameState(null);
          setMe((m) => (m ? { ...m, credits: m.credits - result.credits } : m));
        } else {
          setProblem(result.childMessage);
        }
      } catch {
        setProblem('The studio is having a moment. Try that again!');
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  if (problem === 'needs-sign-in') {
    return (
      <SignIn
        onSignedIn={() => {
          setProblem(null);
          // A fresh load is the simplest way to re-run every startup fetch with
          // the new token, and this happens once per device.
          window.location.reload();
        }}
      />
    );
  }

  if (!me) return <main className="shell shell--centred"><p>Opening the studio…</p></main>;

  if (!me.kid.isActive) {
    return (
      <main className="shell shell--centred">
        <h1>The studio is closed right now</h1>
        <p>Ask a grown-up when you can come back.</p>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="bar">
        <h1>{me.kid.name}’s Studio</h1>
        <div className="bar__right">
          <span className="credits" title="Credits left this week">
            ⚡ {me.credits}
          </span>
          {me.session && <JamTimer endsAt={me.session.endsAt} />}
        </div>
      </header>

      <nav className="tabs">
        <button className={tab === 'make' ? 'tab tab--on' : 'tab'} onClick={() => goToTab('make')}>
          Make something
        </button>
        <button className={tab === 'arcade' ? 'tab tab--on' : 'tab'} onClick={() => goToTab('arcade')}>
          The Arcade
        </button>
        {me.kid.profile !== 'sprout' && (
          <button className={tab === 'pitch' ? 'tab tab--on' : 'tab'} onClick={() => goToTab('pitch')}>
            Pitch Mode
          </button>
        )}
      </nav>

      {tab === 'pitch' && <PitchMode games={myGames} />}

      {tab === 'arcade' && (
        <Arcade
          gamesOrigin={GAMES_ORIGIN}
          onRemixed={(buildId, gameUrl) => {
            // A remix drops the child straight into the studio with the copy
            // open, so the next thing they do is make it theirs.
            setMade({ buildId, gameUrl, brief: EMPTY_BRIEF, templateId: 'platformer' });
            goToTab('make');
          }}
        />
      )}

      {tab === 'make' && !busy && me.session && me.theme && !made && me.kid.profile !== 'sprout' && (
        <div className="coach">
          <strong>This week's theme: {me.theme.title}</strong>
          <p>{me.theme.hint}</p>
        </div>
      )}

      {tab === 'make' && !busy && !made && !me.session && (
        <StartJam
          theme={me.theme}
          minutesLeftThisWeek={me.minutesLeftThisWeek}
          credits={me.credits}
          onStarted={() => api.getMe().then(setMe).catch(() => {})}
        />
      )}

      {tab === 'make' && busy && <BuildingScreen what={buildingWhat} />}

      {tab === 'make' && !busy && !made && me.session && me.kid.profile === 'sprout' && (
        <SproutForm
          busy={busy}
          onSubmit={(brief) =>
            run({ brief, templateId: me.templates[0]?.id ?? 'collector', kind: 'build' })
          }
        />
      )}

      {tab === 'make' && !busy && !made && me.session && me.kid.profile !== 'sprout' && (
        <BriefForm
          templates={me.templates}
          busy={busy}
          onSubmit={(brief, templateId) => run({ brief, templateId, kind: 'build' })}
        />
      )}

      {tab === 'make' && problem && <p className="problem">{problem}</p>}

      {tab === 'make' && !busy && made && (
        <section className="made">
          <GameFrame gameUrl={made.gameUrl} onStateChange={setGameState} />

          {coach?.question && me.kid.profile !== 'sprout' && (
            <div className="coach">
              <strong>One thing that would make this better:</strong>
              <p>{coach.question}</p>
            </div>
          )}

          {gameState && canOfferRepair(gameState) && (
            <div className="repair">
              <p>That one came out broken — that’s on us, not you.</p>
              <button
                disabled={busy}
                onClick={() =>
                  run({
                    brief: made.brief,
                    templateId: made.templateId,
                    kind: 'repair',
                    parentBuildId: made.buildId,
                    instruction: gameState.error?.message ?? 'The game threw an error on load.',
                  })
                }
              >
                Fix it (free)
              </button>
            </div>
          )}

          {tunables.length > 0 && (
            <TweakPanel
              tunables={tunables}
              busy={busy}
              onFocusLine={(line) => {
                // Dragging a slider opens the code, so the connection between
                // the dial and the line is made without anyone explaining it.
                if (line !== null) setShowCode(true);
                setFocusLine(line);
              }}
              onApply={async (changes) => {
                setBusy(true);
                try {
                  const result = await api.applyTweaks(made.buildId, changes);
                  if (result.ok) {
                    setMade({ ...made, buildId: result.buildId, gameUrl: result.gameUrl });
                    setTunables(result.tunables);
                    setSource(null);
                  } else {
                    setProblem(result.childMessage ?? 'That did not work. Try another.');
                  }
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}

          <div className="made__actions">
            <button onClick={() => setShowCode((s) => !s)}>
              {showCode ? 'Hide the code' : 'Show me the code'}
            </button>
            <button
              onClick={async () => {
                const title = made.brief.world || 'My game';
                await api.publishBuild(made.buildId, title);
                setProblem('Shipped to the Arcade! 🚀');
                goToTab('arcade');
              }}
            >
              Ship it
            </button>
            <button onClick={() => { setMade(null); setCoach(null); setProblem(null); }}>
              Make another
            </button>
          </div>

          {showCode && source && <CodePanel html={source} focusLine={focusLine} />}
        </section>
      )}
    </main>
  );
}
